import { useEffect, useState } from "react";
import { Table, IconButton, Popover, Whisper } from "rsuite";
import { EditableRowActions, EditableTableCell } from "../../../../components";
import TrashIcon from "@rsuite/icons/Trash";
import { FaLink } from "react-icons/fa";
import { MdError, MdCheckCircle } from "react-icons/md";
import { getStatusMessagesFromArrayString } from "../../../../utils/constants/status.helpers";
import { roles } from "../../../../utils/constants";
import { useAuthenticationService } from "../../../../services/authentication.service";

const { Column, HeaderCell, Cell } = Table;

const FIELD_CONFIG = {
  type: {
    type: "select",
    options: [
      { label: "Regular", value: "Regular" },
      { label: "Arrears", value: "Arrears" },
    ],
    allowClear: false,
  },
  caregiversName: { type: "text" },
  childName: { type: "text" },
  ccahours: { type: "number", step: "1" },
  currentWeek: { type: "number", step: "0.01" },
  swn: { 
    type: "text",
    regex: "\\d", // Only allow digits
    maxLength: 11, // 000-000-000 (9 digits + 2 dashes)
    placeholder: "000-000-000",
    format: (val) => {
      const numbers = val.replace(/\D/g, "").slice(0, 9);
      let formatted = "";
      for (let i = 0; i < numbers.length; i++) {
        if (i > 0 && i % 3 === 0) {
          formatted += "-";
        }
        formatted += numbers[i];
      }
      return formatted;
    }
  },
  paymentDate: { type: "date" },
  periodTo: { type: "date" },
  renewalDate: { type: "date" },
};

export default function WinzEntriesTable({
  paginatedData,
  editingRowId,
  editingData,
  isEditing,
  startEditing,
  saveRow,
  cancelEditing,
  saving,
  handleFieldChange,
  handleLinkChild,
  handleDeleteEntry,
  sortColumn,
  sortType,
  onSortColumn,
  emailData = {},
}) {

  const AuthenticationService = useAuthenticationService();
  const [isAdmin, setIsAdmin] = useState(AuthenticationService.isInAnyRole(roles.ADMINS));

  return (
    <Table
      rowKey={"id"}
      data={paginatedData}
      height={320}
      bordered
      cellBordered
      style={{ width: "100%" }}
      rowClassName="expanded-row-content"
      className="email-entries-table"
      data-testid="winz-entries-table"
      sortColumn={sortColumn}
      sortType={sortType}
      onSortColumn={onSortColumn}
    >
      {emailData && emailData.statusCode !== 300 && (
        <Column width={150} sortable resizable>
          <HeaderCell>Actions</HeaderCell>
          <Cell>
            {(rowData) => (
              
              <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                {isAdmin && (
                <EditableRowActions
                  rowData={rowData}
                  isEditing={isEditing(rowData.id)}
                  onEdit={startEditing}
                  onSave={saveRow}
                  onCancel={cancelEditing}
                  saving={saving}
                />
                )}
                {!isEditing(rowData.id) &&
                  rowData.statusCode &&
                  !(rowData.statusCode >= 500 && rowData.statusCode < 600 && rowData.statusCode > 100) && (
                    <IconButton
                      icon={<FaLink />}
                      size="xs"
                      appearance="subtle"
                      onClick={() => handleLinkChild(rowData)}
                      className={`link-child-button ${!rowData?.childId > 0 ? "unmatched" : ""}`}
                      data-testid={`link-child-${rowData.id}`}
                    />
                  )}
                {!isEditing(rowData.id) && isAdmin && (
                  <IconButton
                    icon={<TrashIcon />}
                    size="xs"
                    appearance="subtle"
                    onClick={() => handleDeleteEntry(rowData)}
                    title="Delete Entry"
                    className="delete-button"
                    data-testid={`delete-entry-${rowData.id}`}
                  />
                )}
              </div>
            )}
          </Cell>
        </Column>
      )}
      <Column width={120} sortable resizable>
        <HeaderCell>Type</HeaderCell>
        <Cell dataKey="type">
          {(rowData) => (
            <EditableTableCell
              rowData={rowData}
              fieldKey="type"
              fieldConfig={FIELD_CONFIG.type}
              editingRowId={editingRowId}
              editingData={editingData}
              onFieldChange={handleFieldChange}
            />
          )}
        </Cell>
      </Column>
      <Column width={180} flexGrow={1} sortable resizable>
        <HeaderCell>Child</HeaderCell>
        <Cell dataKey="childName">
          {(rowData) => (
            <EditableTableCell
              rowData={rowData}
              fieldKey="childName"
              fieldConfig={FIELD_CONFIG.childName}
              editingRowId={editingRowId}
              editingData={editingData}
              onFieldChange={handleFieldChange}
            />
          )}
        </Cell>
      </Column>
      <Column width={180} flexGrow={1} sortable resizable>
        <HeaderCell>Caregiver</HeaderCell>
        <Cell dataKey="caregiversName">
          {(rowData) => (
            <EditableTableCell
              rowData={rowData}
              fieldKey="caregiversName"
              fieldConfig={FIELD_CONFIG.caregiversName}
              editingRowId={editingRowId}
              editingData={editingData}
              onFieldChange={handleFieldChange}
            />
          )}
        </Cell>
      </Column>
      <Column width={100} sortable resizable>
        <HeaderCell>CCA Hours</HeaderCell>
        <Cell dataKey="ccahours">
          {(rowData) => (
            <EditableTableCell
              rowData={rowData}
              fieldKey="ccahours"
              fieldConfig={FIELD_CONFIG.ccahours}
              editingRowId={editingRowId}
              editingData={editingData}
              onFieldChange={handleFieldChange}
            />
          )}
        </Cell>
      </Column>
      <Column width={120} sortable resizable>
        <HeaderCell>Current Week</HeaderCell>
        <Cell dataKey="currentWeek">
          {(rowData) => {
            const isEditingRow = editingRowId === rowData.id;
            if (isEditingRow) {
              return (
                <EditableTableCell
                  rowData={rowData}
                  fieldKey="currentWeek"
                  fieldConfig={FIELD_CONFIG.currentWeek}
                  editingRowId={editingRowId}
                  editingData={editingData}
                  onFieldChange={handleFieldChange}
                />
              );
            }
            // Format as currency when not editing
            let value = rowData.currentWeek;
            let formatted = value;
            const num = parseFloat(value);
            if (!isNaN(num)) {
              formatted = num.toLocaleString("en-NZ", { style: "currency", currency: "NZD" });
            }
            return <span>{formatted}</span>;
          }}
        </Cell>
      </Column>
      <Column width={150} sortable resizable>
        <HeaderCell>SWN</HeaderCell>
        <Cell dataKey="swn">
          {(rowData) => (
            <EditableTableCell
              rowData={rowData}
              fieldKey="swn"
              fieldConfig={FIELD_CONFIG.swn}
              editingRowId={editingRowId}
              editingData={editingData}
              onFieldChange={handleFieldChange}
            />
          )}
        </Cell>
      </Column>
      <Column width={120} sortable resizable>
        <HeaderCell>Payment Date</HeaderCell>
        <Cell dataKey="paymentDate">
          {(rowData) => (
            <EditableTableCell
              rowData={rowData}
              fieldKey="paymentDate"
              fieldConfig={FIELD_CONFIG.paymentDate}
              editingRowId={editingRowId}
              editingData={editingData}
              onFieldChange={handleFieldChange}
            />
          )}
        </Cell>
      </Column>
      <Column width={120} sortable resizable>
        <HeaderCell>Period To</HeaderCell>
        <Cell dataKey="periodTo">
          {(rowData) => (
            <EditableTableCell
              rowData={rowData}
              fieldKey="periodTo"
              fieldConfig={FIELD_CONFIG.periodTo}
              editingRowId={editingRowId}
              editingData={editingData}
              onFieldChange={handleFieldChange}
            />
          )}
        </Cell>
      </Column>
      <Column width={140} sortable resizable>
        <HeaderCell>Renewal Date</HeaderCell>
        <Cell dataKey="renewalDate">
          {(rowData) => (
            <EditableTableCell
              rowData={rowData}
              fieldKey="renewalDate"
              fieldConfig={FIELD_CONFIG.renewalDate}
              editingRowId={editingRowId}
              editingData={editingData}
              onFieldChange={handleFieldChange}
            />
          )}
        </Cell>
      </Column>
      <Column sortable resizable dataKey="statusCodes">
        <HeaderCell>Status</HeaderCell>
        <Cell dataKey="statusCodes">
          {(rowData) => {
            const statusMsg = getStatusMessagesFromArrayString(rowData.errorCodes);
            const hasErrorStatus = statusMsg && statusMsg.trim() !== "" && statusMsg !== "0" && !statusMsg.toLowerCase().includes("success");
            return (
              <>
                {hasErrorStatus ? (
                  <Whisper placement="auto" followCursor speaker={<Popover>{statusMsg}</Popover>}>
                    <span>
                      <MdError className="error-icon" />
                    </span>
                  </Whisper>
                ) : (
                  <span>
                    <MdCheckCircle className="success-icon" />
                  </span>
                )}
              </>
            );
          }}
        </Cell>
      </Column>
    </Table>
  );
}
